import api from './client';
import { bots } from '../../data/bots';
import type { AdminAnalyticsResponse, UsageStats } from '../../types/api';
import { createLogger } from '../../utils/logger';

const logger = createLogger('analytics-api');

interface ProcessedUsage extends UsageStats {
  botTitle: string;
}

export const getAdminAnalytics = async (startDate: string, endDate: string): Promise<AdminAnalyticsResponse> => {
  try {
    logger.info('[analytics-api] Fetching admin analytics', { startDate, endDate });

    const response = await api.get<any, AdminAnalyticsResponse>('/api/admin/analytics', {
      params: { startDate, endDate }
    });

    if (!response || !response.success) {
      throw new Error('Failed to fetch analytics data');
    }

    // Process usage data to include bot details and ensure numeric values
    const processedUsage: ProcessedUsage[] = response.data.usage.map((usage: UsageStats) => {
      const bot = bots.find(b => b.id === usage.botId) || 
                 bots.flatMap(b => b.subBots || []).find(sb => sb.id === usage.botId);
      
      return {
        ...usage,
        botTitle: bot?.title || 'Unknown Bot',
        creditsUsed: Number(usage.creditsUsed) || 0
      };
    });

    // Calculate totals
    const totalUsage = processedUsage.length;
    const totalRevenue = processedUsage.reduce((sum: number, item: ProcessedUsage) => sum + item.creditsUsed, 0);

    // Calculate usage by bot
    const usageByBot = processedUsage.reduce((acc: Record<string, number>, item: ProcessedUsage) => {
      acc[item.botId] = (acc[item.botId] || 0) + 1;
      return acc;
    }, {});

    // Calculate revenue by day
    const revenueByDay = processedUsage.reduce((acc: Record<string, number>, item: ProcessedUsage) => {
      const date = new Date(item.timestamp).toISOString().split('T')[0];
      acc[date] = (acc[date] || 0) + item.creditsUsed;
      return acc;
    }, {});

    logger.info('[analytics-api] Analytics data processed', {
      totalUsers: response.data.totalUsers,
      totalRevenue,
      totalUsage,
      botCount: Object.keys(usageByBot).length
    });

    return {
      success: true,
      data: {
        totalUsers: response.data.totalUsers,
        totalRevenue,
        totalUsage,
        usageByBot,
        revenueByDay,
        usage: processedUsage
      }
    };
  } catch (error) {
    logger.error('[analytics-api] Failed to fetch admin analytics', error);
    throw error;
  }
};